import { InfoCircleOutlined } from '@ant-design/icons';
import { Button } from '@mm/components';
import PrimarySelect, {
  DropdownOption,
} from '@mm/components/legacy-components/PrimarySelect/PrimarySelect';
import { landmineNames } from '@mm/constants/landmine-names';
import { GoogleAPI } from '@mm/plugins';
import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import {
  GoogleAccount,
  GoogleSheet,
  GoogleSpreadsheet,
} from '@mm/state/interfaces/googleSheet';
import {
  selectSheets,
  selectUserGoogleAccountsWithSheetSupport,
} from '@mm/state/selectors/googleSheetSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useGuardedConnectOAuthToken } from '@mm/stateful-components/OAuth/use-guarded-connect-o-auth-token';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import css from './GoogleSheetPickerForm.module.css';

type GoogleSheetPickerFormProps = {
  setGoogleSheetProps: (optins) => void;
  withLookupColumnSelect?: boolean;
};

export const GoogleSheetPickerForm: React.FC<GoogleSheetPickerFormProps> = ({
  setGoogleSheetProps = () => {},
  withLookupColumnSelect = false,
}) => {
  const [selectedAccount, setSelectedAccount] = useState<GoogleAccount>();
  const [accountNotAuthorized, setAccountNotAuthorized] = useState(false);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const { getAccounts, getSheets, getAccessToken, getOauthUrl, getColumns } =
    useGoogleSheetsCRUDActions();

  const availableAccounts = useSelector(
    selectUserGoogleAccountsWithSheetSupport,
  );
  const accountOptions = [
    { id: 0, email: '+ New Account' } as GoogleAccount,
    ...availableAccounts,
  ];

  const sheets = useSelector(selectSheets);
  const [selectedSpreadsheet, setSelectedSpreadsheet] =
    useState<GoogleSpreadsheet>();
  const [selectedSheet, setSelectedSheet] = useState<GoogleSheet>();
  const [lookupColumnName, setLookupColumnName] = useState<DropdownOption>();
  const [columnsNames, setColumnsNames] = useState<DropdownOption[]>([]);

  const { connect: connectOAuth } = useGuardedConnectOAuthToken(
    'nativeIntegrations',
    landmineNames.INTEGRATIONS_CONTACT_SUPPRESSION,
    'googleOAuth',
    'googleAccountConnected',
    getOauthUrl,
    async () => {
      const accounts = await getAccounts(facebookPageId, {
        sortByUpdatedAtDesc: true,
      });
      setAccountNotAuthorized(false);
      if (accounts && accounts.length > availableAccounts.length) {
        setSelectedAccount(accounts[0]);
      }
    },
  );

  const onSelectSheet = async (item: GoogleSheet) => {
    setSelectedSheet(item);
    if (selectedAccount && selectedSpreadsheet && item) {
      setGoogleSheetProps({
        sheet: item,
        spreadsheet: selectedSpreadsheet,
        account: selectedAccount,
      });
    }
  };

  const onSelectColumn = (item: DropdownOption) => {
    setLookupColumnName(item);
    if (selectedAccount && selectedSpreadsheet && item) {
      setGoogleSheetProps({
        sheet: item,
        spreadsheet: selectedSpreadsheet,
        account: selectedAccount,
        lookupColumnName: item.name,
      });
    }
  };

  const showPicker = async () => {
    if (!selectedAccount) return;

    let token;
    try {
      token = await getAccessToken(facebookPageId, selectedAccount.id, true);
    } catch (e) {
      if (e.status === 401) {
        setAccountNotAuthorized(true);
      }
    }
    if (token) {
      GoogleAPI.showPicker(token, (id, name) => {
        if (selectedSpreadsheet?.id !== id) {
          setSelectedSheet(undefined);
        }
        setSelectedSpreadsheet({ id, name });
        getSheets(selectedAccount.id, id);
      });
    }
  };

  const onSelectAccount = (item: GoogleAccount) => {
    if (selectedAccount?.id !== item.id) {
      setSelectedSpreadsheet(undefined);
      setSelectedSheet(undefined);
    }
    if (item.id === 0) {
      return connectOAuth();
    }

    setSelectedAccount(item);
  };

  const isReadOnlyAccount = false;
  const googleSheetTitle = '';

  useEffect(() => {
    const fetchAccounts = async () => {
      if (availableAccounts.length === 0) {
        await getAccounts(facebookPageId, {
          sortByUpdatedAtDesc: true,
        });
      }
    };

    fetchAccounts();
  }, []);

  useEffect(() => {
    const fetchColumns = async () => {
      if (
        selectedAccount &&
        selectedSpreadsheet &&
        selectedSheet &&
        selectedSheet.title
      ) {
        const response = await getColumns(
          selectedAccount.id,
          selectedSpreadsheet.id,
          selectedSheet.title,
        );
        setColumnsNames(
          response.columns.map(column => ({ id: column, name: column })),
        );
      }
    };

    fetchColumns();
  }, [selectedSheet]);

  return (
    <div className={css.blocks}>
      <div className={css.dropdown}>
        <div>
          <span className={css.attention}>Choose an account</span>
        </div>

        <PrimarySelect
          className={css.primarySelect}
          activeItemClassName={css.activeItemSelect}
          placeholder="Account"
          options={accountOptions}
          value={selectedAccount}
          onChange={item => onSelectAccount(item as GoogleAccount)}
          displayNameKey="email"
          dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
          customDropdownStyles={{ zIndex: '2147483647' }}
          disabled={isReadOnlyAccount}
        />

        {accountNotAuthorized && (
          <div className={css.error}>
            <span>Account is not authorized</span>
            <Button onClick={() => connectOAuth()}>Reauthorize</Button>
          </div>
        )}
      </div>

      <div className={css.dropdown}>
        <span className={css.attention}>Choose Spreadsheet</span>
        <div className={css.chooseSpreadsheet}>
          {selectedSpreadsheet ? (
            <>
              <div>{selectedSpreadsheet.name}</div>
              <Button
                size="sm"
                color="transparent-no-shadow"
                onClick={showPicker}
              >
                Change
              </Button>
            </>
          ) : (
            <Button onClick={showPicker} disabled={!selectedAccount}>
              Choose Spreadsheet
            </Button>
          )}
        </div>
      </div>

      <div className={css.dropdown}>
        <span className={css.attention}>Choose a Worksheet</span>
        {!selectedSheet && (
          <div className={css.info}>
            <InfoCircleOutlined className={css.infoIcon} />
            <div className={css.infoTitle}>
              In order to proceed, please create a worksheet in Google Sheets
            </div>
          </div>
        )}
        <PrimarySelect
          className={css.primarySelect}
          activeItemClassName={css.activeItemSelect}
          placeholder={googleSheetTitle ? googleSheetTitle : 'Worksheet'}
          displayNameKey="title"
          options={sheets}
          value={selectedSheet as DropdownOption}
          onChange={item => onSelectSheet(item as unknown as GoogleSheet)}
          dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
          disabled={
            isReadOnlyAccount || !selectedAccount || !selectedSpreadsheet
          }
          customDropdownStyles={{ zIndex: '2147483647' }}
        />
      </div>

      {withLookupColumnSelect && (
        <div className={css.dropdown}>
          <span className={css.attention}>Choose a Lookup Column</span>
          <PrimarySelect
            className={css.primarySelect}
            activeItemClassName={css.activeItemSelect}
            placeholder="Choose a Lookup Column"
            options={columnsNames}
            value={lookupColumnName}
            onChange={item => onSelectColumn(item)}
            dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
            disabled={
              isReadOnlyAccount ||
              !selectedAccount ||
              !selectedSpreadsheet ||
              !selectedSheet
            }
            customDropdownStyles={{ zIndex: '2147483647' }}
          />
        </div>
      )}
    </div>
  );
};
